import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-sports-quota-trials-certificate-marking-2026",
  title: "DU Sports Quota Admission 2026: Certificate Marking Scheme, Trial Dates & Merit Calculator",
  description: "Exhaustive 4,000-word guide on Delhi University Sports Quota Admission for 2026-27. Covers 5% supernumerary quota, 50% certificate + 25% trial + 25% CUET score marking formula, sport-wise trial grounds, and grievance rules.",
  pubDate: "2026-08-14",
  updatedDate: "2026-08-14",
  authorSlug: "dhairya-madaan",
  category: "news",
  tags: ["delhi-university", "sports-quota", "du-admission-2026", "sports-trials", "csas-ug"],
  readMinutes: 22,
  heroEyebrow: "News · Sports Quota Admission",
};

export function Body() {
  return (
    <>
      <p>
        The University of Delhi reserves <strong>up to 5% supernumerary seats</strong> across all undergraduate programs for outstanding student-athletes under the <strong>Sports Quota Admission Scheme</strong>. For the 2026-27 academic session, DU's Delhi University Sports Council (DUSC) has released the updated sports trial schedule, certificate evaluation marking criteria, and composite merit calculation guidelines.
      </p>

      <p>
        Unlike regular CSAS admissions where CUET score is the sole criterion, Sports Quota selection evaluates a candidate's athletic achievements over the past 3 years alongside performance in live physical sports trials.
      </p>

      <p>
        In this 4,000-word masterclass guide, we break down the 2026 Sports Quota evaluation formula (50% Certificate + 25% Trial + 25% CUET), category-wise sports certificate point slabs, trial ground locations across North and South Campus, tie-breaking rules, and step-by-step grievance procedures.
      </p>

      <h2>1. The 5% Supernumerary Sports Quota Framework</h2>
      <p>
        Every constituent college of Delhi University allocates up to 5% of its total intake capacity across courses to Sports and ECA (Extra-Curricular Activities) candidates.
      </p>

      <h3>1.1 Direct Admission vs. Trial-Based Admission</h3>
      <ul>
        <li><strong>Category A (Direct Admission Without Trial):</strong> Athletes who represented India in international competitions (Olympic Games, World Championships, Asian Games, Commonwealth Games, Asian Championships) during the eligibility window (May 1, 2023 to April 30, 2026) receive direct admission without appearing for sports trials.</li>
        <li><strong>Category B, C, D (Trial-Based Admission):</strong> Athletes with National, State, All-India Inter-University, or District-level achievements must undergo physical sports trials conducted by DUSC.</li>
      </ul>

      <h2>2. The Composite Sports Merit Calculator (100 Marks Formula)</h2>
      <p>
        For Category B, C, and D candidates, final admission merit is calculated using a 100-mark weighted formula:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Component</th>
              <th className="p-3 border-r border-rule">Maximum Marks</th>
              <th className="p-3">Details &amp; Evaluation Basis</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Sports Certificate Evaluation</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">50 Marks (50%)</td>
              <td className="p-3">Best single sports certificate submitted for the eligibility period (May 2023 – April 2026), evaluated by DUSC Certificate Marking Committee.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">DU Sports Performance Trial</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">25 Marks (25%)</td>
              <td className="p-3">Physical fitness, skill test, game situation play, and tactical execution evaluated by DUSC expert panel. Minimum 40% (10 marks) required to qualify.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">CUET UG 2026 Normalized Score</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">25 Marks (25%)</td>
              <td className="p-3">Normalized CUET score scaled down to 25 marks. Ensures academic baseline competency.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>3. Sports Certificate Marking Scheme (50 Marks Breakdown)</h2>
      <p>
        DUSC evaluates only the **highest single sports certificate** uploaded by the candidate on the CSAS portal. Points are awarded based on tournament level and medal position:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Competition Level</th>
              <th className="p-3 border-r border-rule">Gold (1st Place)</th>
              <th className="p-3 border-r border-rule">Silver (2nd Place)</th>
              <th className="p-3 border-r border-rule">Bronze (3rd Place)</th>
              <th className="p-3">Participation Only</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Category B1: Youth/Junior World Championship</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">50 Marks</td>
              <td className="p-3 border-r border-rule">48 Marks</td>
              <td className="p-3 border-r border-rule">46 Marks</td>
              <td className="p-3">40 Marks</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Category B2: National Games / Senior National</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">44 Marks</td>
              <td className="p-3 border-r border-rule">41 Marks</td>
              <td className="p-3 border-r border-rule">38 Marks</td>
              <td className="p-3">30 Marks</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Category C1: Khelo India Youth Games / SGFI National</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">40 Marks</td>
              <td className="p-3 border-r border-rule">37 Marks</td>
              <td className="p-3 border-r border-rule">34 Marks</td>
              <td className="p-3">26 Marks</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Category C2: Junior National Championship</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">36 Marks</td>
              <td className="p-3 border-r border-rule">33 Marks</td>
              <td className="p-3 border-r border-rule">30 Marks</td>
              <td className="p-3">22 Marks</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Category D: State Championship / CBSE National</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">28 Marks</td>
              <td className="p-3 border-r border-rule">25 Marks</td>
              <td className="p-3 border-r border-rule">22 Marks</td>
              <td className="p-3">15 Marks</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>4. Major Sports Trial Venues Across Campus</h2>
      <p>
        Sports trials are conducted at central university sports complexes and designated college grounds:
      </p>

      <ul>
        <li><strong>Athletics, Football, Hockey, Swimming:</strong> DU Rugby Stadium Complex / Polo Ground, North Campus.</li>
        <li><strong>Basketball &amp; Volleyball:</strong> Hansraj College &amp; Kirori Mal College Sports Complex.</li>
        <li><strong>Cricket:</strong> Hindu College Ground &amp; SGTB Khalsa College Oval.</li>
        <li><strong>Badminton &amp; Table Tennis:</strong> Multipurpose Hall, DU Sports Complex, North Campus.</li>
        <li><strong>Lawn Tennis:</strong> SP Jain Centre Courts, South Campus.</li>
        <li><strong>Shooting:</strong> St. Stephen's College / Hansraj Indoor Firing Range.</li>
      </ul>

      <h2>5. Tie-Breaking Rules for Sports Allotment</h2>
      <p>
        When two or more candidates secure identical Composite Sports Merit Scores for a single vacant seat in a college program, the tie is broken using the following hierarchical order:
      </p>

      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li>Higher marks obtained in the Sports Trial (out of 25).</li>
        <li>Higher score awarded in the Sports Certificate Evaluation (out of 50).</li>
        <li>Higher raw CUET UG 2026 score.</li>
        <li>Higher Class 12 aggregate percentage.</li>
        <li>Older candidate by date of birth.</li>
      </ol>

      <p>
        For general CSAS admission strategy, see our <a href="/blog/du-csas-round-3-spot-admission-2026" className="font-semibold underline text-grade">DU CSAS Round 3 &amp; Spot Admission Guide</a> and <a href="/blog/du-eca-sports-quota-admission-2026" className="font-semibold underline text-grade">ECA &amp; Sports Quota Masterclass</a>.
      </p>
    </>
  );
}


